import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import VisitorsIcon from 'assets/Icons/sidebarIcons/VisitorsIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveVisitor from './crud/useSave';
import { VisitorProps } from 'services/hooks/visitor-service';
import Modal from 'components/elements/Modal';
import RoleSelectionForm from './roleSelectionForm';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import CustomPhoneInput from 'components/elements/Form/Input/PhoneInput';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: (newVisitor: VisitorProps) => Promise<VisitorProps>;
  updateQuery: (uuid: string | undefined, updatedVisitor: VisitorProps) => Promise<VisitorProps>;
  editQuery?: (uuid: string | undefined) => Promise<VisitorProps>;
  editUUID?: string;
  queryName: string;
};

/* The code block is defining a validation schema using the `yup` library for a form in a React
component. If these validation rules are not met, an error message will be displayed.*/
const CreateVisitorSchema = yup.object().shape({
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .test('is-possible-phone-number', 'Phone number is not valid', (value) =>
      value ? isPossiblePhoneNumber(value) : true
    ),
  buildingUuid: yup.string().required('Building is required'),
  email: yup.string().when({
    is: (exists: string) => !!exists,
    then: yup.string().email().required('Email is required'),
    otherwise: yup.string().notRequired()
  })
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getVisitorResult, setGetVisitorResult] = useState<VisitorProps | null>(null);
  const [disabled, setdisabled] = useState(false);

  const { isSuccess, data, refetch } = useQuery<VisitorProps | undefined, Error>(
    'visitor',
    () => (editUUID ? editQuery?.(editUUID) : undefined),
    {
      enabled: editUUID ? true : false,
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form
validation. */
  const methods = useForm({
    defaultValues: getVisitorResult
      ? {
          uuid: getVisitorResult?.uuid,
          phoneNumber: getVisitorResult?.phoneNumber,
          email: getVisitorResult?.email,
          firstName: getVisitorResult?.firstName,
          lastName: getVisitorResult?.lastName,
          reason: getVisitorResult?.reason,
          tag: getVisitorResult?.tag,
          company: getVisitorResult?.company,
          buildingUuid: getVisitorResult?.buildingUuid
        }
      : {
          uuid: '',
          phoneNumber: '',
          email: '',
          firstName: '',
          lastName: '',
          reason: '',
          tag: '',
          company: '',
          buildingUuid: ''
        },
    resolver: yupResolver(CreateVisitorSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      refetch();
      setGetVisitorResult(data);
      /* `methods.reset(data)` is being used to set the initial values of the form fields to the values retrieved from the `data` object. The 
      `data` object contains the Visitor details fetched from the API when editing an existing visitor. By calling `methods.reset()` with the 
      desired initial values, the form fields will be populated with the fetched data when the form is rendered. */
      methods.reset({
        ...data,
        phoneNumber: data.phoneNumber ? '+' + data.phoneNumber : ''
      });
    }
  }, [data, editUUID, methods, refetch]);

  /* The `saveVisitorMutation` variable is created using the `useSaveVisitor` hook. This hook is used to
  handle the mutation (create or update) of a visitor. It takes in several parameters: */
  const saveVisitorMutation = useSaveVisitor({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setdisabled(false);
      if (setEditUUID) setEditUUID('');
    }
  });

  /* The function onSubmit is used to save visitor information and disable the submit button.
   * The parameter `e` is of type `VisitorProps`. It is used to represent the event object that is passed to the `onSubmit` function. */
  const onSubmit = async (e: VisitorProps) => {
    setdisabled(true);

    saveVisitorMutation.mutate({
      uuid: e?.uuid,
      phoneNumber: e?.phoneNumber?.replace(/^\+/, ''),
      email: e?.email,
      firstName: e?.firstName,
      lastName: e?.lastName,
      reason: e?.reason,
      tag: e?.tag,
      company: e?.company,
      buildingUuid: e?.buildingUuid
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <RoleSelectionForm Controller={Controller} methods={methods} />

            <Input
              label="First Name"
              name="firstName"
              type="text"
              // data-testid="example"
              placeholder="Enter First Name"
              autoFocus
              methods={methods}
            />
            <Input
              label="Last Name"
              name="lastName"
              type="text"
              // data-testid="example"
              placeholder="Enter Last Name"
              autoFocus
              methods={methods}
            />
            <CustomPhoneInput
              name="phoneNumber"
              label="Phone Number"
              defaultCountry="CA"
              control={methods.control}
              errors={methods.formState.errors}
              placeholder="Enter Phone Number"
            />
            <Input
              label="Email"
              name="email"
              type="email"
              // data-testid="example"
              placeholder="Enter Email"
              autoFocus
              methods={methods}
            />
            <Input
              label="Reason"
              name="reason"
              type="text"
              // data-testid="example"
              placeholder="Enter Reason"
              autoFocus
              methods={methods}
            />
            <Input
              label="Tag"
              name="tag"
              type="text"
              // data-testid="example"
              placeholder="Enter Tag"
              autoFocus
              methods={methods}
            />
            <Input
              label="Company"
              name="company"
              type="text"
              // data-testid="example"
              placeholder="Enter Company"
              autoFocus
              methods={methods}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            // onClick={onClick}
            iconLeft={<VisitorsIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
